import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  PREISART_TEXT,
  type AktionsErgebnis,
  type FeldWert,
  type KonfigurationsFeld,
  type KonfigurationsZustand,
} from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { euro } from "../lib/regeln";
import { FeldEingabe } from "./FeldEingabe";
import { KonfiguratorAktionenKontext, type KonfiguratorAktionen } from "./konfigurator/FeldAktionen";
import { KonfiguratorWizard, type ErledigteAnfrage } from "./konfigurator/KonfiguratorWizard";
import { PreisUebersicht } from "./konfigurator/PreisUebersicht";

/** Nach dieser Pause ohne Eingabe geht die Änderung an den Server. */
const VERZOEGERUNG_MS = 300;

/** Ergebnis, wenn eine Aktion am Protokoll oder am Netz scheitert (der Server meldet Fachliches selbst mit 200). */
const AKTION_NICHT_AUSFUEHRBAR: AktionsErgebnis = { stufe: "fehler", text: "Aktion gerade nicht ausführbar", zusatz: [] };

/** Behält die bisherige Preis-Card – das Nähatelier wird gesondert angeschaut. */
const BISHERIGE_PREISCARD = new Set(["naehatelier"]);

/** Werte, wie der Server sie gemeldet hat – leere Felder als "". */
function werteAus(felder: KonfigurationsFeld[]): Record<string, FeldWert> {
  const werte: Record<string, FeldWert> = {};
  for (const feld of felder) werte[feld.schluessel] = feld.zustand.wert ?? "";
  return werte;
}

/**
 * Rendert den Konfigurator eines Produkts rein aus den Antworten von
 * POST …/konfiguration – ohne einen einzigen Hersteller zu kennen. Genau so
 * arbeitet eine fremde Anwendung: Welche Felder sichtbar, gesperrt oder Pflicht
 * sind, bestimmt der Server nach jeder Eingabe neu.
 */
export function ProduktKonfigurator({
  hersteller,
  produktId,
  onGeladen,
  onErgebnis,
  startWerte,
}: {
  hersteller: string;
  produktId: string;
  /** Meldet den Produktnamen, sobald die erste Antwort da ist – etwa für die Navigation. */
  onGeladen?: (name: string) => void;
  /** Meldet jeden Zustand mit Preis – etwa, um ihn in eine Kalkulation zu übernehmen. */
  onErgebnis?: (stand: KonfigurationsZustand) => void;
  /** Vorbelegte Werte beim ersten Aufruf, z. B. eine Länge aus der Kalkulation. */
  startWerte?: Record<string, FeldWert>;
}) {
  const [stand, setStand] = useState<KonfigurationsZustand | null>(null);
  const [werte, setWerte] = useState<Record<string, FeldWert>>({});
  const [menge, setMenge] = useState(1);
  const [fehler, setFehler] = useState<string | null>(null);
  const [rechnet, setRechnet] = useState(false);
  /** Zählt Werteänderungen und Konfigurationsantworten – Aktionsergebnisse verfallen damit. */
  const [aktionsStand, setAktionsStand] = useState(0);
  /** Zuletzt beendete Anfrage – kommt im selben Rendervorgang wie ihr Stand beim Wizard an. */
  const [erledigt, setErledigt] = useState<ErledigteAnfrage | null>(null);

  // Refs statt State: Eine Anfrage braucht immer den neuesten Stand, auch zwischen zwei Rendervorgängen.
  const werteRef = useRef<Record<string, FeldWert>>({});
  const mengeRef = useRef(1);
  const konfigurationId = useRef<string | null>(null);
  /** Felder, die seit dem Absenden der laufenden Anfrage geändert wurden. */
  const geaendert = useRef(new Set<string>());
  const laeuft = useRef(false);
  const nochmal = useRef(false);
  /** Zählt Produktwechsel – Antworten zu einem vorher geöffneten Produkt verfallen. */
  const generation = useRef(0);
  /** Nummer der zuletzt gestarteten Anfrage, steigt nur – der Wizard erkennt daran, welche Antwort eine Wahl schon kennt. */
  const anfrageNummer = useRef(0);
  // Liest der Wizard beim Klick: Ein State hinge bis zum nächsten Rendervorgang hinter einer eben gestarteten Anfrage her.
  const gestarteteAnfrage = useCallback(() => anfrageNummer.current, []);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onGeladenRef = useRef(onGeladen);
  onGeladenRef.current = onGeladen;
  const onErgebnisRef = useRef(onErgebnis);
  onErgebnisRef.current = onErgebnis;
  const startWerteRef = useRef(startWerte);
  startWerteRef.current = startWerte;

  const senden = useCallback(async () => {
    // Höchstens eine Anfrage gleichzeitig – jede baut auf der letzten Antwort auf.
    if (laeuft.current) {
      nochmal.current = true;
      return;
    }
    laeuft.current = true;
    nochmal.current = false;
    geaendert.current.clear();
    const meine = generation.current;
    const nummer = ++anfrageNummer.current;
    let uebernommen = false;
    setRechnet(true);

    try {
      const res = await api.api.admin.katalog.hersteller[":hersteller"].produkte[":produktId"].konfiguration.$post({
        param: { hersteller, produktId },
        json: {
          ...(konfigurationId.current ? { konfigurationId: konfigurationId.current } : {}),
          werte: werteRef.current,
          menge: mengeRef.current,
        },
      });
      if (!res.ok) throw new Error(await errorMessage(res, "Konfiguration fehlgeschlagen"));
      const daten = await res.json();
      if (meine !== generation.current || !("felder" in daten)) return;

      konfigurationId.current = daten.konfigurationId;
      // Was während der Anfrage eingegeben wurde, bleibt stehen und geht mit der nächsten raus.
      const neu = werteAus(daten.felder);
      // erfal kürzt Text – ein gerade getipptes Leerzeichen soll nicht verschwinden.
      // Der Server vergleicht getrimmt, der lokale Text gilt also nicht als Änderung.
      for (const [schluessel, wert] of Object.entries(neu)) {
        const lokal = werteRef.current[schluessel];
        if (typeof lokal === "string" && typeof wert === "string" && lokal !== wert && lokal.trim() === wert) neu[schluessel] = lokal;
      }
      for (const schluessel of geaendert.current) {
        const lokal = werteRef.current[schluessel];
        if (lokal !== undefined) neu[schluessel] = lokal;
      }
      werteRef.current = neu;
      setWerte(neu);
      setStand(daten);
      uebernommen = true;
      setAktionsStand((n) => n + 1);
      setFehler(null);
      onGeladenRef.current?.(daten.produkt.name);
      onErgebnisRef.current?.(daten);
    } catch (cause) {
      if (meine === generation.current) setFehler(cause instanceof Error ? cause.message : "Konfiguration fehlgeschlagen");
    } finally {
      if (meine === generation.current) {
        laeuft.current = false;
        setRechnet(false);
        // Im selben Takt wie `setStand` – der Wizard sieht Nummer und neuen Stand im selben Rendervorgang.
        setErledigt({ nummer, uebernommen });
        if (nochmal.current) void senden();
      }
    }
  }, [hersteller, produktId]);

  // Neues Produkt: alles zurücksetzen und den Startzustand holen. Der Start wartet einen Takt:
  // React StrictMode baut den Effekt in der Entwicklung einmal ab und wieder auf – so geht
  // trotzdem nur ein Erstaufruf (bei erfal: nur eine Sitzung) raus.
  useEffect(() => {
    generation.current += 1;
    laeuft.current = false;
    nochmal.current = false;
    geaendert.current.clear();
    konfigurationId.current = null;
    werteRef.current = { ...(startWerteRef.current ?? {}) };
    setWerte(werteRef.current);
    setStand(null);
    setAktionsStand((n) => n + 1);
    setFehler(null);
    const start = setTimeout(() => void senden(), 0);
    return () => {
      clearTimeout(start);
      generation.current += 1;
      clearTimeout(timer.current);
    };
  }, [senden]);

  const spaeterSenden = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void senden(), VERZOEGERUNG_MS);
  }, [senden]);

  function aendern(schluessel: string, wert: FeldWert) {
    geaendert.current.add(schluessel);
    const neu: Record<string, FeldWert> = { ...werteRef.current, [schluessel]: wert };
    // Felder, die vom geänderten abhängen (`leerenBei`), etwa die Stofffarbe beim Stoffwechsel –
    // nur bei echter Änderung: Ein erneuter Klick auf die gewählte Karte lässt sie stehen.
    // Gesendet wird trotzdem, der Wizard wartet für seinen Sprung auf die Antwort.
    if (werteRef.current[schluessel] !== wert) {
      for (const f of stand?.felder ?? []) {
        if (f.leerenBei?.includes(schluessel)) {
          geaendert.current.add(f.schluessel);
          neu[f.schluessel] = "";
        }
      }
    }
    werteRef.current = neu;
    setWerte(werteRef.current);
    setAktionsStand((n) => n + 1);
    spaeterSenden();
  }

  function mengeAendern(neu: number) {
    mengeRef.current = neu;
    setMenge(neu);
    spaeterSenden();
  }

  const gruppen = useMemo(() => {
    const map = new Map<string, KonfigurationsFeld[]>();
    for (const feld of stand?.felder ?? []) map.set(feld.gruppe, [...(map.get(feld.gruppe) ?? []), feld]);
    return [...map.entries()];
  }, [stand]);

  // Eine Aktion rechnet der Server aus dem aktuellen Stand des Clients – auch aus noch nicht gesendeten Eingaben.
  const ausfuehren = useCallback(
    async (feld: string, aktion: string): Promise<AktionsErgebnis> => {
      try {
        const route = api.api.admin.katalog.hersteller[":hersteller"].produkte[":produktId"].felder[":schluessel"].aktionen[":aktion"];
        const res = await route.$post({
          param: { hersteller, produktId, schluessel: feld, aktion },
          json: { konfigurationId: konfigurationId.current, werte: werteRef.current },
        });
        if (!res.ok) return AKTION_NICHT_AUSFUEHRBAR;
        return await res.json();
      } catch {
        return AKTION_NICHT_AUSFUEHRBAR;
      }
    },
    [hersteller, produktId],
  );
  const aktionen = useMemo<KonfiguratorAktionen>(() => ({ ausfuehren, stand: aktionsStand }), [ausfuehren, aktionsStand]);

  if (fehler && !stand) {
    return (
      <>
        <p className="error">{fehler}</p>
        <button type="button" disabled={rechnet} onClick={() => void senden()}>
          {rechnet ? "Versuche erneut …" : "Erneut versuchen"}
        </button>
      </>
    );
  }
  if (!stand) return <p className="muted">Lade Konfigurator …</p>;

  const { ergebnis, meldungen } = stand;
  // erfal-Warnungen stehen schon als Meldung oben – nicht doppelt zeigen.
  const hinweise = ergebnis?.hinweise.filter((h) => !meldungen.warnung?.includes(h) && !meldungen.info?.includes(h)) ?? [];

  return (
    <div className="konfigurator">
      <div className="konfigurator__felder">
        {stand.produkt.bildUrl && (
          <img
            className="produktbild"
            src={stand.produkt.bildUrl}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.hidden = true;
            }}
          />
        )}
        {stand.felder.length === 0 && <p className="muted">Dieses Produkt hat keine konfigurierbaren Felder.</p>}
        {/* Mit Schritten als Wizard, sonst wie bisher nach Gruppen. Felder führen ihre Aktionen über den Kontext aus. */}
        <KonfiguratorAktionenKontext.Provider value={aktionen}>
          {stand.schritte?.length ? (
            <KonfiguratorWizard
              stand={stand}
              werte={werte}
              aendern={aendern}
              rechnet={rechnet}
              gestarteteAnfrage={gestarteteAnfrage}
              erledigteAnfrage={erledigt}
            />
          ) : (
            gruppen.map(([gruppe, felder]) => (
              <fieldset key={gruppe}>
                <legend>{gruppe}</legend>
                {felder.map((feld) => (
                  <FeldEingabe
                    key={feld.schluessel}
                    feld={feld}
                    wert={werte[feld.schluessel]}
                    onChange={(wert) => aendern(feld.schluessel, wert)}
                  />
                ))}
              </fieldset>
            ))
          )}
        </KonfiguratorAktionenKontext.Provider>

        <fieldset>
          <legend>Menge</legend>
          <label className="field">
            <span>Stück</span>
            <input
              type="number"
              min={1}
              max={9999}
              value={menge}
              onChange={(e) => mengeAendern(Math.max(1, Number(e.target.value)))}
            />
          </label>
        </fieldset>
      </div>

      <aside className="konfigurator__preis">
        <h2>Preis</h2>
        {rechnet && <p className="muted small">berechne …</p>}
        {/* Nach einem Fehler gehört der letzte Preis nicht mehr zu den Feldern – deshalb nur Fehler und neuer Versuch. */}
        {fehler ? (
          <>
            <p className="error">{fehler}</p>
            <button type="button" disabled={rechnet} onClick={() => void senden()}>
              {rechnet ? "Versuche erneut …" : "Erneut versuchen"}
            </button>
          </>
        ) : (
          <>
            {meldungen.fehler && <p className="error">{meldungen.fehler}</p>}
            {meldungen.warnung && <p className="warnung">{meldungen.warnung}</p>}
            {meldungen.info && <p className="muted small">{meldungen.info}</p>}
            {!ergebnis && !rechnet && <p className="muted small">Noch kein Preis – Pflichtfelder (*) ausfüllen oder einen Wert ändern.</p>}
            {/* Ein globaler Fehler steht schon als Meldung – nicht noch einmal als Ergebnisfehler zeigen. */}
            {ergebnis && !ergebnis.gueltig && !(ergebnis.fehler && meldungen.fehler?.includes(ergebnis.fehler)) && (
              // Im Wizard fehlt der Preis, bis alle Schritte stehen – das ist ein Hinweis, kein Fehler.
              // Feldfehler stehen dort ohnehin rot am Feld.
              <p className={stand.schritte?.length ? "muted small" : "error"}>{ergebnis.fehler}</p>
            )}

            {ergebnis?.gueltig && (
              <>
                <table className="table table--kompakt">
                  <tbody>
                    {ergebnis.posten.map((posten) => (
                      <tr key={posten.schluessel}>
                        <td>{posten.label}</td>
                        <td className="num">{euro(posten.betragCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!BISHERIGE_PREISCARD.has(hersteller) && ergebnis.preis.anteile.produkt.herstellerpreis ? (
                  <PreisUebersicht preis={ergebnis.preis} />
                ) : (
                  <>
                    <dl className="preiskette">
                      <dt>Herstellerpreis brutto (Rechenkern/API)</dt>
                      <dd>{euro(ergebnis.preis.listenpreisBruttoCents)}</dd>
                      <dt>Hersteller-EK netto</dt>
                      <dd>{euro(ergebnis.preis.ekNettoCents)}</dd>
                      <dt>EK der Filiale netto</dt>
                      <dd>{euro(ergebnis.preis.vkNettoCents)}</dd>
                      <dt>EK der Filiale brutto</dt>
                      <dd>{euro(ergebnis.preis.vkBruttoCents)}</dd>
                      <dt>UVP netto</dt>
                      <dd>{euro(ergebnis.preis.uvpNettoCents)}</dd>
                      <dt className="stark">UVP brutto</dt>
                      <dd className="stark">{euro(ergebnis.preis.uvpBruttoCents)}</dd>
                    </dl>

                    {(
                      [
                        ["Produktanteil", ergebnis.preis.anteile.produkt],
                        ["Stoffanteil", ergebnis.preis.anteile.stoff],
                      ] as const
                    )
                      .filter(([, a]) => a !== null)
                      .map(([name, a]) => (
                        <div key={name}>
                          <p className="muted small">{name}</p>
                          <dl className="preiskette">
                            {a!.herstellerpreis && (
                              <>
                                <dt>Herstellerpreis ({PREISART_TEXT[a!.herstellerpreis.preisart]})</dt>
                                <dd>{euro(a!.herstellerpreis.cents)}</dd>
                                <dt>Rabatt</dt>
                                <dd>
                                  {a!.herstellerpreis.rabattProzent.toLocaleString("de-DE")} %
                                  {a!.herstellerpreis.rabattDynamisch ? " (dynamisch)" : ""}
                                </dd>
                                {a!.herstellerpreis.teuerungCents !== undefined && (
                                  <>
                                    <dt>Teuerungszuschlag ({(a!.herstellerpreis.teuerungProzent ?? 0).toLocaleString("de-DE")} %)</dt>
                                    <dd>{euro(a!.herstellerpreis.teuerungCents)}</dd>
                                  </>
                                )}
                                <dt>UVP des Herstellers netto</dt>
                                <dd>{a!.herstellerpreis.herstellerUvpNettoCents === null ? "—" : euro(a!.herstellerpreis.herstellerUvpNettoCents)}</dd>
                                <dt>UVP des Herstellers brutto</dt>
                                <dd>{a!.herstellerpreis.herstellerUvpBruttoCents === null ? "—" : euro(a!.herstellerpreis.herstellerUvpBruttoCents)}</dd>
                              </>
                            )}
                            <dt>Hersteller-EK</dt>
                            <dd>{euro(a!.ekNettoCents)}</dd>
                            <dt>Aufschlag</dt>
                            <dd>{a!.aufschlagProzent.toLocaleString("de-DE")} %</dd>
                            <dt>EK der Filiale</dt>
                            <dd>{euro(a!.filialEkNettoCents)}</dd>
                            <dt>UVP netto</dt>
                            <dd>{euro(a!.uvpNettoCents)}</dd>
                          </dl>
                        </div>
                      ))}

                    {menge > 1 && (
                      <p className="gesamt">
                        Gesamt ({menge} Stück): <strong>{euro(ergebnis.gesamtBruttoCents)}</strong>
                      </p>
                    )}
                  </>
                )}
                {hinweise.map((h) => (
                  <p key={h} className="muted small">
                    {h}
                  </p>
                ))}
              </>
            )}
          </>
        )}

        {stand.grenzwerte.length > 0 && (
          <>
            <h3>Grenzwerte</h3>
            <ul className="muted small">
              {stand.grenzwerte.map((g) => (
                <li key={g.label}>
                  {g.label}: {g.wert}
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
